import dotsIcon from "@tabler/icons/outline/dots.svg";
import eyeOffIcon from "@tabler/icons/outline/eye-off.svg";
import eyeIcon from "@tabler/icons/outline/eye.svg";
import infoCircleIcon from "@tabler/icons/outline/info-circle.svg";
import trashIcon from "@tabler/icons/outline/trash.svg";
import React, { useEffect, useMemo, useState } from "react";
import { defineMessages, useIntl } from "react-intl";

import { useDeleteStatus } from "@/api/statuses/hooks/useDeleteStatus";
import { useAuth } from "@/contexts/auth/useAuth";
import { useModalContext } from "@/contexts/modal-context";
import { cn } from "@/utils/cn";
import { config } from "@/utils/config";
import { defaultMediaVisibility } from "@/utils/status";

import DropdownMenu from "../dropdown-menu";
import { Button, HStack, Text, Popover, Stack } from "../ui";

import type { Status as StatusEntity } from "@/schemas";

const messages = defineMessages({
  delete: { id: "status.delete", defaultMessage: "Delete" },
  deleteConfirm: {
    id: "confirmations.delete.confirm",
    defaultMessage: "Delete",
  },
  deleteHeading: {
    id: "confirmations.delete.heading",
    defaultMessage: "Delete Truth",
  },
  deleteMessage: {
    id: "confirmations.delete.message",
    defaultMessage: "Are you sure you want to delete this Truth?",
  },
  hide: { id: "moderation_overlay.hide", defaultMessage: "Hide content" },
  sensitiveTitle: {
    id: "status.sensitive_warning",
    defaultMessage: "Sensitive content",
  },
  underReviewTitle: {
    id: "moderation_overlay.title",
    defaultMessage: "Content Under Review",
  },
  underReviewSubtitle: {
    id: "moderation_overlay.subtitle",
    defaultMessage:
      "This Truth has been sent to Moderation for review and is only visible to you. If you believe this is an error please contact Support.",
  },
  sensitiveSubtitle: {
    id: "status.sensitive_warning.subtitle",
    defaultMessage: "This content may not be suitable for all audiences.",
  },
  contact: { id: "moderation_overlay.contact", defaultMessage: "Contact" },
  show: { id: "moderation_overlay.show", defaultMessage: "Show Content" },
  moderationAppealTitle: {
    id: "moderation_appeal.title",
    defaultMessage: "Moderation Appeal",
  },
  moderationAppealBody: {
    id: "moderation_appeal.body",
    defaultMessage:
      'If you\'d like to appeal this decision, please email {email} and include your username and "APPEAL" in the subject line.',
  },
});

interface SensitiveContentOverlayProps {
  status: StatusEntity;
  onToggleVisibility?(): void;
  visible?: boolean;
  ref?: React.Ref<HTMLDivElement>;
}

const SensitiveContentOverlay = (props: SensitiveContentOverlayProps) => {
  const { onToggleVisibility, status, ref } = props;

  const { currentAccount } = useAuth();
  const intl = useIntl();
  const { openModal } = useModalContext();

  const deleteStatus = useDeleteStatus();

  const emails = config.emails;
  const isUnderReview = status.visibility === "self";
  const isOwnStatus = status.account?.id === currentAccount?.id;
  const displayMedia = currentAccount?.settings?.displayMedia as string;

  const [visible, setVisible] = useState<boolean>(
    defaultMediaVisibility(status, displayMedia),
  );

  const toggleVisibility = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (onToggleVisibility) {
      onToggleVisibility();
    } else {
      setVisible((prevValue) => !prevValue);
    }
  };

  const handleDeleteStatus = () => {
    const deleteModal = currentAccount?.settings?.deleteModal;
    if (!deleteModal) {
      deleteStatus.mutate({ id: status.id });
    } else {
      openModal("CONFIRM", {
        heading: intl.formatMessage(messages.deleteHeading),
        message: intl.formatMessage(messages.deleteMessage),
        confirm: intl.formatMessage(messages.deleteConfirm),
        onConfirm: () => deleteStatus.mutate({ id: status.id }),
      });
    }
  };

  const menu = useMemo(() => {
    return [
      {
        text: intl.formatMessage(messages.delete),
        action: handleDeleteStatus,
        icon: trashIcon,
        destructive: true,
      },
    ];
  }, []);

  useEffect(() => {
    if (typeof props.visible !== "undefined") {
      setVisible(!!props.visible);
    }
  }, [props.visible]);

  return (
    <div
      className={cn("absolute z-40", {
        "inset-0 flex h-full w-full cursor-default justify-center rounded-lg border-0 bg-gray-800/75 backdrop-blur-lg":
          !visible,
        "right-1 bottom-1": visible,
      })}
      data-testid="sensitive-overlay"
    >
      {visible ? (
        <Button
          text={intl.formatMessage(messages.hide)}
          icon={eyeOffIcon}
          onClick={toggleVisibility}
          variant="default"
          size="sm"
          testId="toggle-button"
        />
      ) : (
        <div className="flex max-h-screen items-center justify-center">
          <div className="mx-auto w-3/4 space-y-4 text-center" ref={ref}>
            <div className="space-y-1">
              <Text theme="white" weight="semibold">
                {intl.formatMessage(
                  isUnderReview
                    ? messages.underReviewTitle
                    : messages.sensitiveTitle,
                )}
              </Text>

              <Text theme="white" size="sm" weight="medium">
                {intl.formatMessage(
                  isUnderReview
                    ? messages.underReviewSubtitle
                    : messages.sensitiveSubtitle,
                )}
              </Text>
            </div>

            <HStack alignItems="center" justifyContent="center" space={2}>
              {!isUnderReview ? (
                <>
                  {emails.appeals && (
                    <Popover
                      interaction="click"
                      containerClassName="max-w-[305px]"
                      content={
                        <Stack space={2}>
                          <Text weight="semibold">
                            {intl.formatMessage(messages.moderationAppealTitle)}
                          </Text>
                          <Text>
                            {intl.formatMessage(messages.moderationAppealBody, {
                              email: emails.appeals,
                            })}
                          </Text>
                        </Stack>
                      }
                    >
                      <Button
                        type="button"
                        variant={null}
                        className="border-gray-100 bg-transparent text-gray-100 hover:bg-white/10 dark:border-white/10"
                        size="sm"
                        icon={infoCircleIcon}
                      >
                        {intl.formatMessage(messages.contact)}
                      </Button>
                    </Popover>
                  )}
                </>
              ) : null}

              <Button
                type="button"
                variant={null}
                className="border-gray-100 bg-transparent text-gray-100 hover:bg-white/10 dark:border-white/10"
                size="sm"
                icon={eyeIcon}
                onClick={toggleVisibility}
                testId="toggle-button"
              >
                {intl.formatMessage(messages.show)}
              </Button>

              {isUnderReview && isOwnStatus ? (
                <DropdownMenu items={menu} src={dotsIcon} />
              ) : null}
            </HStack>
          </div>
        </div>
      )}
    </div>
  );
};

export default SensitiveContentOverlay;
